// /////////////////////////////////////////////////////////////
// [ファイル] sourceQuality.ts
// [概要] ソース品質管理プラットフォームのデータモデル型定義。
//        ソースをシンボル単位 (関数 / クラス / メソッド / コンポーネント等)
//        で分解し、各シンボルに品質メタ情報 (AI要約・課題・状態・タグ) を
//        ぶら下げる。フェーズ1 (MVP) はサンプルデータで UI を駆動するが、
//        この型はそのままバックエンド (Firestore) のスキーマに対応させる。
// /////////////////////////////////////////////////////////////

/** レビュー状態。UI のステータスバッジ・絞り込みに対応。 */
export type ReviewStatus = 'unreviewed' | 'in_review' | 'reviewed' | 'needs_fix';

/** シンボルの種別。パーサ (ts-morph / ast) が判定する。 */
export type SymbolKind =
  | 'function'
  | 'class'
  | 'method'
  | 'react_component'
  | 'hook'
  | 'const';

/** 課題の重要度。 */
export type IssueSeverity = 'high' | 'medium' | 'low';

/** 課題の対応状態。 */
export type IssueStatus = 'open' | 'resolved';

/** 取り込み元プロジェクト (フロント / バックエンド)。 */
export type SourceProject = 'frontend' | 'functions-node' | 'functions-python';

/** シンボルに紐づく課題 (CRUD 対象)。 */
export interface SourceIssue {
  id: string;
  title: string;
  body?: string;
  severity: IssueSeverity;
  status: IssueStatus;
  created_by?: string;
  created_at?: string;
}

/** シンボルに紐づく AI 分析コメント。 */
export interface AiComment {
  id: string;
  /** 'summary' | 'quality' | 'suggestion' など。 */
  type: string;
  body: string;
  created_at?: string;
}

/** シンボル単位の管理レコード (画面の中心単位)。 */
export interface SourceSymbol {
  symbol_id: string;
  module_id: string;
  file_path: string;
  /** ファイルパス + 修飾名 (例 MyClass.myMethod, useFoo)。差分突合のキー。 */
  qualified_name: string;
  name: string;
  kind: SymbolKind;
  language: string;
  signature?: string;
  /** シンボルのコード断片 (ファイル丸ごとではない)。 */
  code_snapshot: string;
  ai_summary?: string | null;
  complexity?: number | null;
  review_status: ReviewStatus;
  assignee?: string | null;
  tags: string[];
  issues: SourceIssue[];
  ai_comments: AiComment[];
}

/** ファイル単位のメタ。symbols をぶら下げる。 */
export interface SourceModule {
  module_id: string;
  file_path: string;
  language: string;
  project: SourceProject;
  symbols: SourceSymbol[];
}

// ----- 表示用ラベル (色は描画側で解決) -----

export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  unreviewed: '未レビュー',
  in_review: 'レビュー中',
  reviewed: 'レビュー済',
  needs_fix: '要修正',
};

export const SYMBOL_KIND_LABEL: Record<SymbolKind, string> = {
  function: '関数',
  class: 'クラス',
  method: 'メソッド',
  react_component: 'コンポーネント',
  hook: 'フック',
  const: '定数',
};

export const ISSUE_SEVERITY_LABEL: Record<IssueSeverity, string> = {
  high: '高',
  medium: '中',
  low: '低',
};

export const REVIEW_STATUS_ORDER: ReviewStatus[] = [
  'unreviewed',
  'in_review',
  'reviewed',
  'needs_fix',
];
